import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { USER_ROLES } from '../utils/constants';

export const useRBACStore = create(
  persist(
    (set, get) => ({
      roles: [],
      privileges: new Set(),
      isSuperAdmin: false,

      setRolesAndPrivileges: (rolesPrivileges = []) => {
        const activeRoles = rolesPrivileges.filter(
          (r) => r.status?.toLowerCase() === 'active'
        );

        const roleNames = activeRoles.map((r) => r.name);

        const isSuperAdmin = roleNames.some((r) => r === USER_ROLES.SUPERADMIN);

        const allPrivileges = isSuperAdmin
          ? []
          : activeRoles.flatMap((role) =>
              (role.privilege || []).map((p) => p.name.toLowerCase())
            );

        set({
          roles: activeRoles.map((r) => r.name),
          privileges: new Set(allPrivileges),
          isSuperAdmin,
        });
      },

      hasRole: (roleName) => {
        const { isSuperAdmin, roles } = get();
        if (isSuperAdmin) return true;
        return roles.includes(roleName);
      },

      hasAnyRole: (roleList = []) => {
        const { isSuperAdmin, roles } = get();
        if (isSuperAdmin) return true;
        return roleList.some((r) => roles.includes(r));
      },

      hasPrivilege: (privName) => {
        const { isSuperAdmin, privileges } = get();
        if (isSuperAdmin) return true;
        return privileges.has(privName.toLowerCase());
      },

      hasAnyPrivilege: (privList = []) => {
        const { isSuperAdmin, privileges } = get();
        if (isSuperAdmin) return true;
        return privList.some((p) => privileges.has(p.toLowerCase()));
      },

      hasAllPrivileges: (privList = []) => {
        const { isSuperAdmin, privileges } = get();
        if (isSuperAdmin) return true;
        return privList.every((p) => privileges.has(p.toLowerCase()));
      },

      clear: () =>
        set({ roles: [], privileges: new Set(), isSuperAdmin: false }),
    }),
    {
      name: 'rbac-storage',
      partialize: (state) => ({
        roles: state.roles,
        privileges: Array.from(state.privileges),
        isSuperAdmin: state.isSuperAdmin,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.privileges = new Set(state.privileges);
        }
      },
    }
  )
);
